// Utility to pre-cache gallery images for offline use

const isDev = process.env.NODE_ENV === 'development';

const devLog = (...args) => {
  if (isDev) {
    console.log(...args);
  }
};

const devWarn = (...args) => {
  if (isDev) {
    console.warn(...args);
  }
};

const devError = (...args) => {
  if (isDev) {
    console.error(...args);
  }
};

/**
 * Pre-cache all gallery images when the app loads
 * This ensures images are available offline
 */
export const preCacheGalleryImages = async (images) => {
  if (!('caches' in window)) {
    devWarn('Cache API not available in this browser');
    return false;
  }

  try {
    const cache = await caches.open('images-v1.2.0');
    
    // Extract image URLs from gallery images
    const imageUrls = images.map(img => {
      // Handle both string URLs and imported modules
      if (typeof img.src === 'string') {
        return img.src;
      }
      // For webpack imports, the src is already the URL
      return img.src;
    }).filter(Boolean);

  devLog('Pre-caching gallery images:', imageUrls.length, 'images');

    // Cache all images
    const cachePromises = imageUrls.map(async (url) => {
      try {
        // Check if already cached
        const cachedResponse = await cache.match(url);
        if (cachedResponse) {
          devLog('Already cached:', url);
          return;
        }

        // Fetch and cache the image
        const response = await fetch(url, {
          mode: 'cors',
          credentials: 'same-origin'
        });

        if (response && response.ok) {
          await cache.put(url, response);
          devLog('Cached image:', url);
        }
      } catch (error) {
        devWarn('Failed to cache image:', url, error);
      }
    });

    await Promise.all(cachePromises);
    devLog('Gallery images pre-cached successfully');
    return true;
  } catch (error) {
    devError('Failed to pre-cache gallery images:', error);
    return false;
  }
};

/**
 * Clear image cache (useful for updates)
 */
export const clearImageCache = async () => {
  try {
    const deleted = await caches.delete('images-v1.2.0');
    devLog('Image cache cleared:', deleted);
    return deleted;
  } catch (error) {
    devError('Failed to clear image cache:', error);
    return false;
  }
};

/**
 * Get cache size and stats
 */
export const getImageCacheStats = async () => {
  try {
    const cache = await caches.open('images-v1.2.0');
    const keys = await cache.keys();
    
    return {
      count: keys.length,
      urls: keys.map(req => req.url)
    };
  } catch (error) {
    devError('Failed to get cache stats:', error);
    return { count: 0, urls: [] };
  }
};
